import { Link, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

// ── Single source of truth for company / contact details ──
export const COMPANY = {
  legal_name: "StartBhargava (OPC) Private Limited",
  brand: "Tablo",
  cin: "U70200TS2026OPC215000",
  pan: "ABTCS5423J",
  incorporated_on: "23 April 2026",
  director: "Shivraj Ranjeri",
  registered_office:
    "H.No 3-25/E/502, EIPL Skyila, Puppalaguda Village, Rangareddy, Telangana — 500089, India",
  support_email: "support@mytablo.app",
  support_phone: "+91 8121 222 333",
  website: "https://mytablo.app",
};

const LAST_UPDATED = "29 May 2026";

function LegalShell({ title, eyebrow, children, testid }) {
  return (
    <div className="min-h-screen bg-cream text-ink" data-testid={testid}>
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-8 sm:py-12">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-light-ink hover:text-ink mb-6" data-testid="legal-back-link">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.6} /> Back to Tablo
        </Link>
        <p className="text-amber text-xs uppercase tracking-[0.18em] font-medium mb-2">{eyebrow}</p>
        <h1 className="font-serif text-4xl sm:text-5xl leading-tight">{title}</h1>
        <p className="text-light-ink text-xs mt-3">Last updated: {LAST_UPDATED}</p>
        <div className="mt-8 max-w-none [&_h2]:font-serif [&_h2]:text-2xl [&_h2]:mt-8 [&_h2]:mb-3 [&_h2]:text-ink [&_p]:text-light-ink [&_p]:leading-relaxed [&_p]:mt-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_ul]:my-3 [&_li]:text-light-ink [&_li]:leading-relaxed [&_a]:text-amber [&_a]:underline [&_strong]:text-ink [&_em]:text-ink/80 [&_table]:w-full [&_table]:my-4 [&_table]:text-sm [&_th]:text-left [&_th]:text-xs [&_th]:uppercase [&_th]:tracking-wider [&_th]:text-light-ink [&_th]:p-2 [&_th]:border-b [&_th]:border-tablo-border [&_td]:p-2 [&_td]:border-b [&_td]:border-tablo-border/60 [&_td]:text-light-ink">
          {children}
        </div>
        <footer className="mt-12 pt-6 border-t border-tablo-border text-xs text-light-ink space-y-1">
          <p><strong className="text-ink">{COMPANY.legal_name}</strong> (operating <em>{COMPANY.brand}</em>)</p>
          <p>CIN: {COMPANY.cin} · PAN: {COMPANY.pan}</p>
          <p>Registered office: {COMPANY.registered_office}</p>
          <p>Support: <a href={`mailto:${COMPANY.support_email}`}>{COMPANY.support_email}</a> · {COMPANY.support_phone}</p>
        </footer>
      </div>
    </div>
  );
}

export function PrivacyPolicy() {
  return (
    <LegalShell title="Privacy Policy" eyebrow="Legal" testid="page-privacy">
      <p>
        This Privacy Policy explains how <strong>{COMPANY.legal_name}</strong> ("we", "us", "{COMPANY.brand}")
        collects, uses, stores, and shares the personal information of guests, restaurant partners, and other users of
        the {COMPANY.brand} platform (mobile site, web app, and partner dashboards).
      </p>

      <h2>1. Information we collect</h2>
      <ul>
        <li><strong>Account data</strong> — name, phone number, email, organisation (optional), role (booker / restaurant partner / admin).</li>
        <li><strong>Booking data</strong> — group size, date/time, budget, dietary preferences (allergens, spice tolerance, veg preference, drink preferences), notes, restaurant choices.</li>
        <li><strong>Payment metadata</strong> — deposit amount, transaction reference, status. Card / UPI details are processed by our payment partner and are <strong>not</strong> stored on {COMPANY.brand} servers.</li>
        <li><strong>Device data</strong> — IP address, browser/OS, session identifiers (used for security and analytics).</li>
        <li><strong>Communications</strong> — WhatsApp / email / SMS messages we send (booking confirmations, reconfirmation calls, support).</li>
      </ul>

      <h2>2. How we use your information</h2>
      <ul>
        <li>To process and confirm your booking with the restaurant partner.</li>
        <li>To curate menus based on your dietary preferences and budget.</li>
        <li>To call you 1–2 hours before your slot for reconfirmation (handled by the {COMPANY.brand} Ops team — your phone number is <strong>not</strong> shared with the restaurant).</li>
        <li>To send transactional notifications (booking confirmed, deposit paid, reconfirmation reminders).</li>
        <li>To improve product quality and prevent fraud.</li>
      </ul>

      <h2>3. What we share with restaurants</h2>
      <p>
        Restaurant partners only see: your <strong>first name + last initial</strong>, organisation name (if any),
        group size, date/time, dietary briefing (allergens, spice level, veg preference), and notes. We
        intentionally <strong>do not share your phone number or email</strong> with the partner — all reconfirmation
        calls are made by {COMPANY.brand} Ops.
      </p>

      <h2>4. Third-party processors</h2>
      <p>
        We use Razorpay (payments), WhatsApp Business API / Gupshup (messaging), email service providers (transactional
        email), MongoDB Atlas (hosting), and Google Cloud (infrastructure). Each is contractually bound to the same
        level of data protection. We do not sell your data to advertisers.
      </p>

      <h2>5. Data retention</h2>
      <p>
        Booking, invoice, and tax records are retained for at least <strong>8 years</strong> as required under Indian
        GST and Companies Act regulations. Account data is retained until you ask us to delete it (see Section 7).
      </p>

      <h2>6. Cookies & analytics</h2>
      <p>
        We use a small number of first-party cookies for session management. We do not use third-party advertising
        trackers.
      </p>

      <h2>7. Your rights</h2>
      <p>
        You can request a copy of your data, correction of inaccuracies, or deletion of your account (subject to legal
        retention obligations) by emailing <a href={`mailto:${COMPANY.support_email}`}>{COMPANY.support_email}</a>. We
        respond within 30 days.
      </p>

      <h2>8. Children</h2>
      <p>
        {COMPANY.brand} is not intended for users under 18. We do not knowingly collect data from minors.
      </p>

      <h2>9. Security</h2>
      <p>
        We use TLS in transit and encryption at rest. Phone OTPs and tokens are short-lived. Despite our best efforts,
        no system is 100% secure — if you suspect a breach of your account, email us immediately.
      </p>

      <h2>10. Updates to this policy</h2>
      <p>
        We may update this policy from time to time. Material changes will be highlighted on this page with a refreshed
        "Last updated" date.
      </p>

      <h2>11. Grievance officer</h2>
      <p>
        For privacy complaints, write to our Grievance Officer at{" "}
        <a href={`mailto:${COMPANY.support_email}`}>{COMPANY.support_email}</a>. As per the Information Technology
        (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011, we
        respond within 30 days.
      </p>
    </LegalShell>
  );
}

export function Terms() {
  return (
    <LegalShell title="Terms & Conditions" eyebrow="Legal" testid="page-terms">
      <p>
        These Terms govern your use of <strong>{COMPANY.brand}</strong>, a group-dining booking platform operated by{" "}
        <strong>{COMPANY.legal_name}</strong> (CIN {COMPANY.cin}). By using {COMPANY.brand} you agree to these Terms.
      </p>

      <h2>1. Nature of service</h2>
      <p>
        {COMPANY.brand} is a marketplace that connects guests with restaurants and banquet venues for group dining
        experiences. {COMPANY.brand} facilitates discovery, menu curation (AI + manual), package selection, deposit
        collection, and reconfirmation. The actual food, service, and venue experience is provided by the restaurant
        partner — {COMPANY.brand} is not the restaurateur.
      </p>

      <h2>2. Eligibility</h2>
      <p>
        You must be at least 18 years old to book. Group size limits: minimum 5 guests for dining bookings and minimum
        100 for banquet bookings, up to a maximum of 500.
      </p>

      <h2>3. Bookings</h2>
      <ul>
        <li><strong>Package bookings</strong> need to be made at least <strong>8 hours</strong> before the slot so the
        kitchen has time to prep. Guests are reconfirmed by {COMPANY.brand} Ops at T-3 hours.</li>
        <li><strong>À-la-carte bookings</strong> can be made closer to the slot. Food preparation begins when you check
        in at the venue.</li>
        <li>A booking is <strong>confirmed</strong> only after the restaurant accepts and the 10% deposit is paid.</li>
      </ul>

      <h2>4. Pricing & deposit</h2>
      <p>
        {COMPANY.brand} charges a <strong>10% deposit on the final bill</strong> (not the intent budget) at the time of
        booking confirmation. The deposit is <strong>fully adjusted</strong> against your final bill at the venue.
      </p>
      <p>
        Package prices are set by the restaurant and may vary by group size (pax tiers). Menu prices shown are
        inclusive of applicable GST unless stated otherwise.
      </p>

      <h2>5. Conduct & no-show</h2>
      <ul>
        <li>If you arrive more than <strong>30 minutes late</strong>, the restaurant may release your table.</li>
        <li>No-shows forfeit the deposit. We define a no-show as not arriving within 60 minutes of the slot start.</li>
        <li>Cancellation slabs: <strong>4 hrs+ = 100% refund</strong>, <strong>1–4 hrs = 50%</strong>, <strong>&lt; 1 hr or no-show = no refund</strong>. See the <a href="/refunds">Refund Policy</a> for details.</li>
        <li>GST <strong>18%</strong> is charged on the booking total. A GST invoice is issued if you provide a valid GSTIN at checkout.</li>
        <li>Damage to restaurant property or violation of venue policy is between you and the restaurant.</li>
      </ul>

      <h2>6. Modifications</h2>
      <p>
        You can modify pax (within the same tier) and time up to <strong>8 hours before</strong> a package booking, or
        up to <strong>1 hour before</strong> an à-la-carte booking, subject to slot availability.
      </p>

      <h2>7. Liability</h2>
      <p>
        {COMPANY.brand} is not liable for food quality, allergic reactions, service issues, or any damage caused at the
        venue. We facilitate the booking; the restaurant remains the service provider. Our maximum liability for any
        claim is limited to the deposit you paid for that booking.
      </p>

      <h2>8. Restaurant partner terms</h2>
      <p>
        Restaurant partners agree to honour confirmed bookings, respect guest dietary briefings, and ensure that menu
        items and prices listed on {COMPANY.brand} are accurate. {COMPANY.brand} reserves the right to pause a partner's
        listing for non-performance, repeated complaints, or material misrepresentation.
      </p>

      <h2>9. Intellectual property</h2>
      <p>
        The {COMPANY.brand} name, logo, and platform are owned by {COMPANY.legal_name}. You may not copy, reverse
        engineer, or build derivative services from our software without written consent.
      </p>

      <h2>10. Termination</h2>
      <p>
        We may suspend or terminate accounts that violate these Terms, attempt fraud, or harass partners/guests.
      </p>

      <h2>11. Governing law & jurisdiction</h2>
      <p>
        These Terms are governed by the laws of India. Any dispute will be subject to the exclusive jurisdiction of the
        courts at Hyderabad, Telangana.
      </p>

      <h2>12. Contact</h2>
      <p>
        For questions about these Terms, email <a href={`mailto:${COMPANY.support_email}`}>{COMPANY.support_email}</a>{" "}
        or call {COMPANY.support_phone}.
      </p>
    </LegalShell>
  );
}

export function RefundPolicy() {
  return (
    <LegalShell title="Refund & Cancellation Policy" eyebrow="Legal" testid="page-refund">
      <p>
        This Refund Policy applies to deposits collected by <strong>{COMPANY.legal_name}</strong> ("{COMPANY.brand}")
        for bookings made on the {COMPANY.brand} platform.
      </p>

      <h2>1. Deposit structure</h2>
      <p>
        {COMPANY.brand} collects a <strong>10% deposit on the final bill</strong> at the time of booking confirmation.
        The remaining 90% (the "balance") is paid directly to the restaurant at the venue.
      </p>

      <h2>2. Cancellation by the guest</h2>
      <table>
        <thead>
          <tr><th>Cancelled</th><th>Refund of deposit</th></tr>
        </thead>
        <tbody>
          <tr><td>4 hrs or more before slot</td><td><strong>100%</strong> refund</td></tr>
          <tr><td>Between 1 hr and 4 hrs before slot</td><td><strong>50%</strong> refund</td></tr>
          <tr><td>Less than 1 hr before slot</td><td><strong>No refund</strong></td></tr>
          <tr><td>No-show</td><td><strong>No refund</strong></td></tr>
        </tbody>
      </table>

      <h2>3. Cancellation by the restaurant</h2>
      <p>
        If the restaurant cancels a confirmed booking for any reason (capacity issue, force majeure, partner pause),
        you receive a <strong>100% refund of the deposit</strong> within 7 working days, and we will offer to rebook
        you at another comparable venue.
      </p>

      <h2>4. Cancellation by Tablo</h2>
      <p>
        In rare cases where {COMPANY.brand} cancels a booking (e.g. payment fraud detected, suspected misuse), the
        deposit is refunded in full within 7 working days.
      </p>

      <h2>5. Group / takeaway bookings</h2>
      <p>
        Large group dine-in and bulk takeaway bookings use the same cancellation slabs above. For pax of 50+, please
        cancel as early as possible so the kitchen can plan — your refund slab is unchanged.
      </p>

      <h2>6. How to request a refund</h2>
      <p>
        Email <a href={`mailto:${COMPANY.support_email}`}>{COMPANY.support_email}</a> with your booking ID and reason,
        or call {COMPANY.support_phone}. We acknowledge within 1 working day and process refunds within{" "}
        <strong>7 working days</strong> to the original payment instrument.
      </p>

      <h2>7. Disputes</h2>
      <p>
        If you have a quality / service complaint about a restaurant after the meal, raise a dispute within 48 hours by
        emailing us with photographs / details. We investigate with the partner and, at our discretion, issue a partial
        refund or rebooking credit.
      </p>

      <h2>8. Force majeure</h2>
      <p>
        For cancellations caused by force majeure (natural disaster, government order, internet outage at the venue),
        we issue full refunds regardless of timing.
      </p>
    </LegalShell>
  );
}

// Default export used by Footer to render a 3-link block in one import
export default function LegalFooterLinks({ className = "" }) {
  const loc = useLocation();
  // Hide on partner & admin shells.
  if (loc.pathname.startsWith("/partner") || loc.pathname.startsWith("/admin")) return null;
  return (
    <footer className={`bg-cream/80 border-t border-tablo-border mt-12 ${className}`} data-testid="legal-footer">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-xs text-light-ink">
        <div>
          <p>
            <strong className="text-ink">{COMPANY.brand}</strong> is operated by {COMPANY.legal_name} · CIN {COMPANY.cin}
          </p>
          <p className="mt-0.5">Support: <a href={`mailto:${COMPANY.support_email}`} className="text-amber underline">{COMPANY.support_email}</a> · {COMPANY.support_phone}</p>
        </div>
        <nav className="flex items-center gap-4">
          <Link to="/privacy" className="hover:text-ink" data-testid="footer-privacy-link">Privacy</Link>
          <Link to="/terms" className="hover:text-ink" data-testid="footer-terms-link">Terms</Link>
          <Link to="/refunds" className="hover:text-ink" data-testid="footer-refunds-link">Refunds</Link>
        </nav>
      </div>
    </footer>
  );
}
